// eslint-disable-next-line import/extensions
import { StreamableHTTPServerTransport } from '@modelcontextprotocol/sdk/server/streamableHttp.js';
import type { Core } from '@strapi/types';
import {
  classifyMcpRequestFailure,
  sendDidNotAuthenticateMcpRequest,
  sendDidNotHandleMcpRequest,
  sendDidUseMcpServer,
} from '../metrics/metrics';
import { sendJsonRpcError } from '../utils/sendJsonRpcError';
import { withTimeout } from '../utils/withTimeout';
import type { McpHandlerDependencies } from './types';

export const createPostHandler = (deps: McpHandlerDependencies): Core.MiddlewareHandler => {
  const {
    strapi,
    authenticationStrategy,
    config,
    createServerWithRegistries,
    capabilityDefinitions,
  } = deps;

  return async (ctx) => {
    // Opt out of Koa's response phase — the MCP SDK writes directly to ctx.res
    // (via res.writeHead / res.end / SSE streaming). Without this, Koa's respond()
    // would also try to write ctx.body to the socket after the handler returns.
    ctx.respond = false;
    const req = ctx.req;
    const res = ctx.res;

    let hadAuthenticatedMcpRequest = false;

    try {
      const authResult = await authenticationStrategy.authenticate(ctx);
      if (authResult.authenticated === false) {
        sendDidNotAuthenticateMcpRequest(strapi, authResult.reason);
        sendJsonRpcError(res, 'AUTHENTICATION_REQUIRED');
        return;
      }

      hadAuthenticatedMcpRequest = true;
      sendDidUseMcpServer(strapi);

      const { mcpServer } = createServerWithRegistries({
        strapi,
        definitions: capabilityDefinitions,
        isDevMode: config.isDevMode(),
        ability: authResult.ability,
        user: authResult.user,
      });

      const transport = new StreamableHTTPServerTransport({
        sessionIdGenerator: undefined,
      });

      try {
        await withTimeout(
          mcpServer.connect(transport),
          config.connectTimeoutMs,
          'mcpServer.connect'
        );

        const requestBody = ctx.request.body ?? null;
        await withTimeout(
          transport.handleRequest(req, res, requestBody),
          config.requestTimeoutMs,
          'transport.handleRequest'
        );
      } finally {
        await mcpServer.close();
      }
    } catch (error) {
      strapi.log.error('[MCP] Error handling POST request', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });

      sendJsonRpcError(res, 'INTERNAL_ERROR');

      if (hadAuthenticatedMcpRequest) {
        sendDidNotHandleMcpRequest(strapi, classifyMcpRequestFailure(error));
      }
    }
  };
};
